import { useEffect, useMemo, useState } from "react";
import {
  LIST_SEARCH_DEBOUNCE_MS,
  readQ,
  setOrDelete,
} from "@/modules/shell/list-url-filters";
import { useListUrlParams } from "@/modules/shell/use-list-url-params";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { SortingState } from "@tanstack/react-table";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { WarehouseSelect } from "@/modules/inventory/components/WarehouseSelect";
import {
  listInventory,
  listWarehouses,
  type InventoryListSort,
} from "@/shared/api/inventory";
import { queryKeys } from "@/shared/query/keys";
import {
  AdminFilters,
  adminFilterControlClass,
} from "@/modules/shell/AdminFilters";
import { ActiveFilterChips } from "@/modules/shell/ActiveFilterChips";
import { DataTable } from "@/modules/shell/data-table";
import {
  DataTableChrome,
  DeskListFilterReset,
} from "@/modules/shell/DataTableChrome";
import { useDeskListFilterReset } from "@/modules/shell/use-desk-list-filter-reset";
import {
  ListSearchStatus,
  listSearchOnlinePending,
} from "@/modules/shell/ListSearchStatus";
import { DeskListHeader } from "@/modules/shell/DeskListHeader";
import { DeskSubNav } from "@/modules/shell/DeskSubNav";
import { stockModuleTabs } from "@/modules/shell/desk-module-tabs";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { inventoryBalanceColumns } from "./inventory-columns";

const PAGE_SIZE = 50;
const SORTABLE: InventoryListSort[] = [
  "productName",
  "sku",
  "warehouseCode",
  "qty",
  "avgCost",
];

function sortingToApi(sorting: SortingState): {
  sort: InventoryListSort;
  sortDir: "asc" | "desc";
} {
  const first = sorting[0];
  if (first && SORTABLE.includes(first.id as InventoryListSort)) {
    return {
      sort: first.id as InventoryListSort,
      sortDir: first.desc ? "desc" : "asc",
    };
  }
  return { sort: "productName", sortDir: "asc" };
}

export function InventoryPage() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const { searchParams, patchParams } = useListUrlParams();
  const warehouseId = searchParams.get("wh");
  const setWarehouseId = (next: string | null) =>
    patchParams((p) => setOrDelete(p, "wh", next ?? ""));
  const [query, setQuery] = useState(() => readQ(searchParams));
  const [debouncedQ, setDebouncedQ] = useState(() => readQ(searchParams));
  const [sorting, setSorting] = useState<SortingState>([]);
  const { sort, sortDir } = sortingToApi(sorting);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const q = query.trim();
      setDebouncedQ(q);
      patchParams((p) => setOrDelete(p, "q", q));
    }, LIST_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query, patchParams]);

  const warehousesQuery = useQuery({
    queryKey: ["warehouses", companySlug, "inventory-filter"],
    enabled: Boolean(companySlug) && Boolean(warehouseId),
    queryFn: () => listWarehouses(companySlug!),
  });

  const inventoryQuery = useInfiniteQuery({
    queryKey: queryKeys.inventory.balances(companySlug ?? "", {
      warehouseId,
      q: debouncedQ,
      limit: PAGE_SIZE,
      sort,
      sortDir,
    }),
    enabled: Boolean(companySlug),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      listInventory(companySlug!, {
        ...(warehouseId ? { warehouseId } : {}),
        q: debouncedQ || undefined,
        limit: PAGE_SIZE,
        cursor: pageParam,
        sort,
        sortDir,
        signal,
      }),
    getNextPageParam: (last) =>
      last.hasMore && last.nextCursor ? last.nextCursor : undefined,
  });

  const errorMsg =
    inventoryQuery.error instanceof Error
      ? inventoryQuery.error.message
      : inventoryQuery.isError
        ? "Failed to load"
        : null;

  useEffect(() => {
    if (errorMsg === "Not signed in") {
      navigate("/login", { replace: true });
    }
  }, [errorMsg, navigate]);

  const columns = useMemo(
    () => (companySlug ? inventoryBalanceColumns(companySlug) : []),
    [companySlug],
  );

  const filtersActive = Boolean(query.trim()) || Boolean(warehouseId);
  const filterReset = useDeskListFilterReset({
    patchParams,
    setQuery: setQuery,
    canResetToDefault: filtersActive,
    canClearAll: filtersActive,
  });
  const searchingOnline = listSearchOnlinePending({
    query,
    debouncedQ,
    isFetching: inventoryQuery.isFetching,
  });

  if (!companySlug) return null;

  const balances =
    inventoryQuery.data?.pages.flatMap((p) => p.balances) ?? [];
  const total = inventoryQuery.data?.pages[0]?.total;
  const loading = inventoryQuery.isPending;
  const error = errorMsg && errorMsg !== "Not signed in" ? errorMsg : null;
  const warehouseLabel =
    warehousesQuery.data?.warehouses.find((w) => w.id === warehouseId)
      ?.name ?? warehouseId;


  return (
    <PageFrame>
      <DataTableChrome>
      <DeskListFilterReset {...filterReset} />
      <DeskListHeader
        title="Inventory"
        description="On-hand by warehouse"
        tabs={
          <DeskSubNav
            placement="header"
            items={stockModuleTabs(companySlug)}
            aria-label="Stock views"
          />
        }
      />

      <AdminFilters>
        <Input
          className={`${adminFilterControlClass} min-w-[12rem] flex-1`}
          placeholder="Search name, SKU, barcode…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={loading}
        />
        <WarehouseSelect
          companySlug={companySlug}
          value={warehouseId}
          onChange={setWarehouseId}
          allowAll
          disabled={loading}
        />
        <ActiveFilterChips
          chips={
            warehouseId
              ? [
                  {
                    key: "wh",
                    label: `Warehouse: ${warehouseLabel}`,
                    onClear: () => setWarehouseId(null),
                  },
                ]
              : []
          }
        />
      </AdminFilters>
      <ListSearchStatus online searching={searchingOnline} />
      {balances.length > 0 && total != null ? (
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          Showing {balances.length}
          {total > balances.length ? ` of ${total}` : ""}
        </p>
      ) : null}

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}
      {loading && (
        <Alert variant="muted" className="mt-8">
          Loading inventory…
        </Alert>
      )}

      {!loading && balances.length === 0 && !error && (
        <div className="mt-8">
          <EmptyState
            title={
              debouncedQ ? `No match for “${debouncedQ}”` : "No on-hand yet"
            }
            description={
              debouncedQ
                ? "Try another name, SKU, or warehouse."
                : "Receive a PO or post a stock adjustment on a product."
            }
            action={
              filtersActive ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => filterReset.onClearAll()}
                >
                  Clear filters
                </Button>
              ) : (
                <Button asChild size="sm">
                  <Link
                    to={`/c/${companySlug}/products`}
                    className="no-underline"
                  >
                    Products
                  </Link>
                </Button>
              )
            }
          />
        </div>
      )}

      {balances.length > 0 ? (
        <>
          <DataTable
            listId="inventory-balances"
            companySlug={companySlug}
            columns={columns}
            data={balances}
            getRowId={(r) => `${r.productId}:${r.warehouseId}`}
            sorting={sorting}
            onSortingChange={setSorting}
            defaultVisibility={{ barcode: false }}
            onRefresh={() => void inventoryQuery.refetch()}
          />
          {inventoryQuery.hasNextPage ? (
            <div className="mt-4 flex justify-center">
              <Button
                type="button"
                variant="outline"
                disabled={inventoryQuery.isFetchingNextPage}
                pending={inventoryQuery.isFetchingNextPage}
                pendingLabel="Loading more…"
                onClick={() => void inventoryQuery.fetchNextPage()}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      <p className="mt-8 text-sm">
        <Link to={`/c/${companySlug}`} className="no-underline">
          ← Company home
        </Link>
      </p>
          </DataTableChrome>
    </PageFrame>
  );
}
